package com.trevorwiebe.timesheet.admin.loadrate.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.FilterChip
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRate
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.LoadRateCategory
import com.trevorwiebe.timesheet.admin.loadrate.domain.model.displayName
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LoadRateScreen(
    viewModel: LoadRateViewModel = koinViewModel(),
    onBack: () -> Unit
) {
    val state by viewModel.state.collectAsState()

    Scaffold(
        topBar = {
            TopBar(
                title = "Load Rates",
                backIcon = { BackIcon() },
                onBack = onBack
            )
        },
        floatingActionButton = {
            FloatingActionButton(
                onClick = { viewModel.onEvent(LoadRateEvent.OnAddClick) },
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary
            ) {
                Icon(Icons.Default.Add, contentDescription = "Add load rate")
            }
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
            CollapsingHeaderLayout(
                header = {
                    Column {
                        LoadRateSearchField(
                            query = state.searchQuery,
                            onQueryChanged = { viewModel.onEvent(LoadRateEvent.OnSearchQueryChanged(it)) }
                        )
                        LoadRateFilterChipRow(
                            activeFilters = state.activeFilters,
                            onFilterToggled = { viewModel.onEvent(LoadRateEvent.OnFilterToggled(it)) }
                        )
                        HorizontalDivider()
                    }
                }
            ) { contentPadding ->
                if (state.isLoading) {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                    }
                } else {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = contentPadding
                    ) {
                        items(state.visibleLoadRates) { loadRate ->
                            LoadRateRow(
                                loadRate = loadRate,
                                locations = state.locations,
                                onClick = { viewModel.onEvent(LoadRateEvent.OnEditClick(loadRate)) }
                            )
                        }
                    }
                }
            }

            if (state.showAddEditDialog) {
                AddEditLoadRateDialog(
                    editing = state.editingLoadRate,
                    locations = state.locations,
                    onDismiss = { viewModel.onEvent(LoadRateEvent.OnDismissDialog) },
                    onSave = { category, type, unit, perUnitText, flatRateText, pickupLocationId, dropLocationId ->
                        viewModel.onEvent(
                            LoadRateEvent.OnSave(
                                rateCategory = category,
                                rateType = type,
                                unit = unit,
                                perUnitText = perUnitText,
                                flatRateText = flatRateText,
                                pickupLocationId = pickupLocationId,
                                dropLocationId = dropLocationId,
                            )
                        )
                    }
                )
            }

            NativeDialog(
                visible = state.error != null,
                title = "Something went wrong",
                message = state.error ?: "",
                confirmText = "OK",
                onConfirm = { viewModel.onEvent(LoadRateEvent.OnErrorShown) },
                onDismiss = { viewModel.onEvent(LoadRateEvent.OnErrorShown) }
            )
        }
    }
}

@Composable
private fun LoadRateSearchField(
    query: String,
    onQueryChanged: (String) -> Unit
) {
    TimesheetTextField(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        text = query,
        placeholder = "Search by pickup or drop location",
        singleLine = true,
        trailingIcon = if (query.isNotEmpty()) {
            {
                IconButton(onClick = { onQueryChanged("") }) {
                    Icon(
                        imageVector = Icons.Default.Close,
                        contentDescription = "Clear search"
                    )
                }
            }
        } else null,
        onTextChange = onQueryChanged
    )
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun LoadRateFilterChipRow(
    activeFilters: Set<LoadRateCategory>,
    onFilterToggled: (LoadRateCategory) -> Unit
) {
    FlowRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
        verticalArrangement = Arrangement.spacedBy((-8).dp)
    ) {
        Text(
            text = "Filter",
            fontSize = 14.sp,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
            modifier = Modifier.align(Alignment.CenterVertically).padding(end = 4.dp)
        )
        LoadRateCategory.entries.forEach { category ->
            val selected = category in activeFilters
            FilterChip(
                selected = selected,
                onClick = { onFilterToggled(category) },
                label = { Text(category.displayName) },
                border = FilterChipDefaults.filterChipBorder(
                    enabled = true,
                    borderColor = MaterialTheme.colorScheme.tertiary,
                    selected = selected
                ),
                colors = FilterChipDefaults.filterChipColors(
                    containerColor = MaterialTheme.colorScheme.background,
                    selectedContainerColor = MaterialTheme.colorScheme.tertiary,
                    selectedLabelColor = MaterialTheme.colorScheme.onTertiary,
                )
            )
        }
    }
}

@Composable
internal fun LoadRateRow(
    loadRate: LoadRate,
    locations: List<Location>,
    onClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                val route = loadRate.routeLabel(locations)
                if (route.isEmpty()) {
                    Text(
                        text = loadRate.label,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.SemiBold
                    )
                } else {
                    Text(
                        text = route,
                        fontSize = 16.sp,
                        fontWeight = FontWeight.SemiBold
                    )
                    Text(
                        text = loadRate.label,
                        fontSize = 14.sp,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
        }
        HorizontalDivider()
    }
}
